import {
    AfterContentInit,
    ContentChildren,
    Directive,
    EventEmitter,
    Input,
    Output,
    QueryList,
} from '@angular/core';
import {SortableDirective} from './sortable.directive';
import Timer = NodeJS.Timer;
import {SlickColumnEx} from "../../stocks/models/slick-column-ex";

export interface SortEvent{
    currentIndex: number;
    newIndex: number;
    drop: boolean;
    event: PointerEvent;
}

const distance = (rectA: ClientRect, rectB: ClientRect): number => {
    return Math.sqrt(
        Math.pow(rectB.top - rectA.top, 2) +
        Math.pow(rectB.left - rectA.left, 2)
    )
};

const hCenter = (rect: ClientRect): number => {
    return rect.left + rect.width / 2;
};

const vCenter = (rect: ClientRect): number => {
    return rect.top + rect.height / 2;
};

@Directive({
    selector: '[appSortableList]'
})
export class SortableListDirective implements AfterContentInit{
    constructor(){
    };

    @ContentChildren(SortableDirective) sortables: QueryList<SortableDirective>;

    @Output() sort = new EventEmitter<SortEvent>();
    @Output() dragEnd = new EventEmitter<SlickColumnEx>();

    @Input('appSortableList')
    set appSortableList(update: boolean){
        if (update){
            this.sortables.forEach(sortable => {
                if (sortable.dragStart.observers.length === 1){
                    this.subscribeDragStart(sortable)
                }

                if (sortable.dragMove.observers.length === 1){
                    this.subscribeDragMove(sortable)
                }

                if (sortable.dragEnd.observers.length === 1){
                    this.subscribeDragEnd(sortable)
                }
            });
        }
    }

    @Input('grid')
    private grid?: HTMLElement;

    @Input('rowHeight')
    private rowHeight?: number;

    @Input('rowsToMove')
    private rowsToMove?: number;

    private clientRects: ClientRect[];
    private guide: JQuery<HTMLElement>;
    private interval: Timer;
    private event: PointerEvent;

    ngAfterContentInit(): void{
        this.sortables.forEach(sortable => {
            this.subscribeDragMove(sortable);
            this.subscribeDragStart(sortable);
            this.subscribeDragEnd(sortable);
        });
    }

    private subscribeDragMove = (sortable): void => {
        sortable.dragMove.subscribe(event => {
            this.event  = event;
            let offsetHeight = sortable.element.nativeElement.offsetHeight,
                parOffsetHeight = sortable.element.nativeElement.parentElement.offsetHeight,
                canvas = sortable.element.nativeElement.parentElement,
                scroll = sortable.element.nativeElement.parentElement.scrollTop;

            let move = this.checkForMove(sortable, event);

            switch (move){
                case 0:
                    this.interval && clearInterval(this.interval);
                    this.interval = null;
                    this.setGuidePosition(sortable, event)
                    break;
                case 1:
                    if (this.interval) return;
                    this.interval = setInterval(() => {
                        scroll += offsetHeight;
                        if ((scroll + parOffsetHeight) > this.sortables.length * offsetHeight){
                            scroll = this.sortables.length * offsetHeight - parOffsetHeight;
                        }
                        canvas.scrollTo({
                            top: scroll,
                            left: 0,
                            behavior: 'smooth'
                        });
                        this.setGuidePosition(sortable, this.event);
                    }, 100);
                    break;
                case 2:
                    if (this.interval) return;
                    this.interval = setInterval(() => {
                        scroll -= offsetHeight
                        if (scroll < 0){
                            scroll = 0
                        }
                        canvas.scrollTo({
                            top: scroll,
                            left: 0,
                            behavior: 'smooth'
                        });
                        this.setGuidePosition(sortable, this.event);
                    }, 100);
                    break;
            }

            this.measureClientRects();
        });
    }

    private readonly calcGridDropZone = (event: PointerEvent): boolean => {
        if (!this.grid){
            return false;
        }
        let rect: DOMRect = this.grid.getBoundingClientRect() as DOMRect;
        return event.x > rect.left && event.x < rect.right && event.y > rect.top && event.y < rect.bottom
    }

    //TODO REFACTOR code 152
    private checkForMove = (sortable: SortableDirective, event: PointerEvent): number => {
        let offsetHeight = this.rowHeight ? this.rowHeight : sortable.element.nativeElement.offsetHeight,
            parOffsetTop = sortable.element.nativeElement.parentElement.getBoundingClientRect().top,
            parOffsetHeight = sortable.element.nativeElement.parentElement.offsetHeight,
            offsetTop = event.clientY,
            rowsToMove = this.rowsToMove ? this.rowsToMove : 2;

        if ((parOffsetTop + parOffsetHeight - rowsToMove * offsetHeight) - offsetTop < 1){
            return 1
        } else if ((offsetTop - parOffsetTop - rowsToMove * offsetHeight) < 1){
            return 2
        }
        return 0
    }

    private subscribeDragStart = (sortable): void => {
        sortable.dragStart.subscribe(event => {
            this.interval && clearInterval(this.interval);
            this.interval = null;
            let canvas = sortable.element.nativeElement.parentElement;
            this.guide = $("<div class='slick-reorder-guide'/>")
                .css("position", "absolute")
                .css("zIndex", "99998")
                .css("width", $(canvas).innerWidth())
                .css("top", -1000)
                .appendTo(canvas);

            this.measureClientRects()
        });

    }

    private subscribeDragEnd = (sortable): void => {
        sortable.dragEnd.subscribe(event => {
            this.calcGridDropZone(event);
            this.interval && clearInterval(this.interval);
            this.interval = null;
            this.calcGridDropZone(event)
                ? this.dragEnd.emit(sortable.item)
                : this.detectSorting(sortable, event, true);
            this.guide.remove();
        });
    }

    private measureClientRects(){
        this.clientRects = this.sortables.map(sortable => sortable.element.nativeElement.getBoundingClientRect());
    }

    private detectSorting(sortable: SortableDirective, event: PointerEvent, drop: boolean){
        // if (this.inDropZone){
        //     return;
        // }
        const currentIndex = this.sortables.toArray().indexOf(sortable);
        const currentRect = this.clientRects[currentIndex];
        let top = this.setGuidePosition(sortable, event, true);
        this.sort.emit({
            currentIndex: currentIndex,
            newIndex: top/currentRect.height,
            drop,
            event
        });
    }

    //TODO REFACTOR CODE 153
    private setGuidePosition = (sortable: SortableDirective, event: PointerEvent, calculate: boolean = false): number => {
        const canvasHeight = this.rowHeight ? this.rowHeight : 20;
        const currentIndex = this.sortables.toArray().indexOf(sortable);
        const currentRect = this.clientRects[currentIndex];
        const currentHeight = this.rowHeight ? this.rowHeight : currentRect.height;
        let canvas = sortable.element.nativeElement.parentElement,
            scroll = canvas.scrollTop,
            top = event.pageY - $(canvas).offset().top > canvas.offsetHeight ? canvas.offsetHeight : event.pageY - $(canvas).offset().top,
            last = Math.abs((scroll % currentHeight) - currentHeight);
        last = last !== canvasHeight ? last : 0;
        let insertBefore = Math.max(0, Math.min(Math.round(top / currentHeight), this.sortables.length)),
            guideTop = insertBefore * currentHeight + scroll + last;
        if (calculate){
            return guideTop;
        }
        guideTop = guideTop === currentHeight * this.sortables.length ? guideTop - 2 : guideTop;
        this.guide.css("top", guideTop);
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/directives/sortable-list.directive.ts